import type { Invoice, Payment } from './types/projects.js';

export type InvoiceStatus = 'unpaid' | 'partial' | 'paid';

/** paidTotal = sum of every Payment actually paid (a deposit is just an early one — no special
 * casing); balanceDue = billedAmount - paidTotal. Payments with status 'scheduled' (queued for
 * whoever issues the check/transfer, not yet sent) are excluded from paidTotal/balanceDue and
 * reported separately as scheduledTotal, so the books only move once money actually leaves. A
 * missing status means 'paid' (every payment recorded before scheduling existed). A pure function
 * reused by both the Lambda (for the /budget rollup) and the frontend (for display), so the two
 * can never drift on how a balance is computed — same reasoning as computeProductLineTotal. */
export function computeInvoiceBalance(
  invoice: Pick<Invoice, 'billedAmount'>,
  payments: Pick<Payment, 'amount' | 'status'>[],
): { paidTotal: number; scheduledTotal: number; balanceDue: number; status: InvoiceStatus } {
  const paidTotal = payments.filter((p) => p.status !== 'scheduled').reduce((sum, p) => sum + p.amount, 0);
  const scheduledTotal = payments.filter((p) => p.status === 'scheduled').reduce((sum, p) => sum + p.amount, 0);
  const balanceDue = invoice.billedAmount - paidTotal;
  const status: InvoiceStatus = paidTotal <= 0 ? 'unpaid' : balanceDue <= 0 ? 'paid' : 'partial';
  return { paidTotal, scheduledTotal, balanceDue, status };
}

export interface EstimateDrawdownInvoice {
  sourceEstimateId?: string;
  taskItemId?: string;
  paidTotal: number;
}

/** How much of an Estimate's accepted-option total is still "estimated" (not yet collected) —
 * the Budget's Estimated column must draw this down as payments come in, or a paid-off estimate
 * would keep double-counting alongside its own Invoiced/Paid totals. Matched by TASK when the
 * estimate is task-linked, not by the explicit "converted from" pointer: `isActive` exclusivity is
 * already scoped per task (see Estimate.isActive), so at most one active estimate can ever own a
 * given task, and every dollar invoiced+paid against that task — whether the invoice was created
 * via "Convert to invoice" or added manually with the same task picked — represents progress
 * against that estimate. Only task-less (project-level) estimates fall back to matching by the
 * literal conversion pointer (`sourceEstimateId`), since without a task anchor there's no
 * unambiguous way to tell which invoices "belong" to a project-level bid. A pure function reused by
 * both the Lambda (for the /budget rollup) and the frontend (for the per-estimate "remaining"
 * display), so the two can never drift. */
export function computeEstimateDrawdown(
  estimate: { id: string; taskItemId?: string },
  invoicesWithPaid: EstimateDrawdownInvoice[],
): number {
  return invoicesWithPaid
    .filter((inv) =>
      estimate.taskItemId ? inv.taskItemId === estimate.taskItemId : inv.sourceEstimateId === estimate.id,
    )
    .reduce((sum, inv) => sum + inv.paidTotal, 0);
}

/** Convenience wrapper: an Estimate's accepted total minus computeEstimateDrawdown, floored at
 * zero (an invoice can be edited to bill more than the original accepted total, which shouldn't
 * go negative here). This is the number that should feed the Budget's Estimated column and any
 * "remaining" display — see computeEstimateDrawdown for the matching rule. */
export function computeEstimateRemaining(
  acceptedTotal: number,
  estimate: { id: string; taskItemId?: string },
  invoicesWithPaid: EstimateDrawdownInvoice[],
): number {
  return Math.max(0, acceptedTotal - computeEstimateDrawdown(estimate, invoicesWithPaid));
}

export interface ScopedInvoiceAmount {
  taskItemId?: string;
  /** The literal "converted from" pointer — takes priority over taskItemId matching below, since
   * an invoice's own task can be edited independently after conversion (very common in practice:
   * a project-level estimate gets converted, then the invoice is tagged to a specific task). */
  sourceEstimateId?: string;
  billedAmount: number;
}

export interface PreInvoiceEstimate {
  estimateId: string;
  taskItemId?: string;
  /** Raw accepted-option total, not netted against anything. */
  acceptedTotal: number;
}

export interface BottomLineResult {
  estimatedRemaining: number;
  contractorEstimatesShare: number;
  selfSourcedMaterialsShare: number;
  totalProjectCost: number;
  remainingBalanceDue: number;
  /** Each scope's own remaining, keyed by taskItemId (or '' for the task-less project-level
   * bucket) — lets a caller show a per-task Total/Due without recomputing the drawdown itself. */
  remainingByScope: Record<string, number>;
}

/** The one project "bottom line" calculation, reused by both ProjectDetailPage (rendering it) and
 * the Lambda's /budget rollup (feeding ClientsPage/ClientDetailPage), so the two can never disagree.
 *
 * Two-pass drawdown:
 *  1. Claim each invoice against the specific Estimate its sourceEstimateId points to (if that
 *     estimate is still in `estimates`), regardless of the invoice's own taskItemId — this is the
 *     only reliable attribution for a task-less estimate, and takes priority even for a task-linked
 *     one, since real usage shows an invoice's task getting edited independently after conversion
 *     (e.g. a project-level bid converted, then the resulting invoice tagged to a specific task —
 *     matching by taskItemId alone would then never net that estimate down, double-counting it
 *     alongside its own "Invoiced" total).
 *  2. Whatever's left of each estimate (after being claimed) is pooled into its scope (a task, or
 *     the task-less project-level bucket) together with that scope's Products total — since once a
 *     single invoice covers a task there's no way to attribute which dollar was labor vs. material
 *     — and that pool is netted against only the *unclaimed* invoices matching that scope (by
 *     taskItemId, or "no taskItemId" for the project-level scope).
 *
 * Total project cost = estimatedRemaining (summed across every scope) + total invoiced (gross);
 * Remaining balance due = that minus total paid. Contractor-vs-self-sourced is a proportional split
 * of estimatedRemaining by each side's RAW (pre-invoice) weight — once a scope's been partially
 * invoiced there's no ledger of which dollar was labor vs. material, so this is an approximation,
 * not a per-source drawdown. `productByScope` is keyed by taskItemId, with `''` as the sentinel key
 * for the task-less project-level scope. */
export function computeBottomLine(
  estimates: PreInvoiceEstimate[],
  productByScope: Record<string, number>,
  invoices: ScopedInvoiceAmount[],
  invoicedTotal: number,
  paidTotal: number,
): BottomLineResult {
  const knownEstimateIds = new Set(estimates.map((e) => e.estimateId));
  const claimedByEstimate = new Map<string, number>();
  for (const inv of invoices) {
    if (inv.sourceEstimateId && knownEstimateIds.has(inv.sourceEstimateId)) {
      claimedByEstimate.set(inv.sourceEstimateId, (claimedByEstimate.get(inv.sourceEstimateId) ?? 0) + inv.billedAmount);
    }
  }
  const unclaimedInvoices = invoices.filter(
    (inv) => !(inv.sourceEstimateId && knownEstimateIds.has(inv.sourceEstimateId)),
  );

  const estimateRemainingByScope: Record<string, number> = {};
  for (const e of estimates) {
    const claimed = claimedByEstimate.get(e.estimateId) ?? 0;
    const remaining = Math.max(0, e.acceptedTotal - claimed);
    const key = e.taskItemId ?? '';
    estimateRemainingByScope[key] = (estimateRemainingByScope[key] ?? 0) + remaining;
  }

  const remainingByScope: Record<string, number> = {};
  let estimatedRemaining = 0;
  for (const key of new Set([...Object.keys(estimateRemainingByScope), ...Object.keys(productByScope)])) {
    const pool = (estimateRemainingByScope[key] ?? 0) + (productByScope[key] ?? 0);
    const invoicedForScope = unclaimedInvoices
      .filter((inv) => (key ? inv.taskItemId === key : !inv.taskItemId))
      .reduce((sum, inv) => sum + inv.billedAmount, 0);
    const remaining = Math.max(0, pool - invoicedForScope);
    remainingByScope[key] = remaining;
    estimatedRemaining += remaining;
  }

  const rawEstimateTotal = estimates.reduce((sum, e) => sum + e.acceptedTotal, 0);
  const rawProductTotal = Object.values(productByScope).reduce((sum, n) => sum + n, 0);
  const rawTotal = rawEstimateTotal + rawProductTotal;
  const contractorEstimatesShare = rawTotal > 0 ? estimatedRemaining * (rawEstimateTotal / rawTotal) : 0;
  const selfSourcedMaterialsShare = estimatedRemaining - contractorEstimatesShare;

  const totalProjectCost = estimatedRemaining + invoicedTotal;
  const remainingBalanceDue = totalProjectCost - paidTotal;

  return {
    estimatedRemaining,
    contractorEstimatesShare,
    selfSourcedMaterialsShare,
    totalProjectCost,
    remainingBalanceDue,
    remainingByScope,
  };
}
